import { createRouter, createWebHistory } from "vue-router";
import routes from "@/router/routes.js";
import { useAuthenticationStore } from "@/stores/authentication";
import { UserPermissionsService } from "@/services/userPermissions";
import { EnvironmentService } from "@/utils/environment";
import { mapRouteEntered } from "@/map-engine/timing";

const router = createRouter({
    history: createWebHistory(),
    routes,
});

// Recover from a stale deploy: after a release, an already-open tab still
// references the previous build's hashed chunk files, which no longer exist on
// the server — every lazy import then fails ("Failed to load module script")
// until the page is reloaded. Reload once to pick up the new build; the time
// guard stops a reload loop when the failure has some other cause.
const CHUNK_RELOAD_KEY = "chunk-reload-at";
const CHUNK_ERROR_RE = /dynamically imported module|module script/i;

function reloadForStaleChunk(targetUrl) {
    const lastReload = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY)) || 0;
    if (Date.now() - lastReload < 30000) {
        return false;
    }
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
    window.location.assign(targetUrl);
    return true;
}

// Vite fires this for any dynamic import (or css) that fails to load.
window.addEventListener("vite:preloadError", (event) => {
    if (reloadForStaleChunk(window.location.href)) {
        event.preventDefault();
    }
});

// Lazy route components rejected during navigation surface here instead.
router.onError((error, to) => {
    if (CHUNK_ERROR_RE.test(error?.message || "")) {
        reloadForStaleChunk(to.fullPath);
    }
});

// Map load timing (M2.2): starts when a map route is entered for a venue not
// already on screen; the map component stops it when its engine is ready.
// Registered first, so the auth checks below count towards the load.
router.beforeEach((to, from) => {
    if (
        to.meta.mapView &&
        (!from.meta.mapView || from.params.venueId !== to.params.venueId)
    ) {
        mapRouteEntered(from.matched.length === 0);
    }
});

// Navigation guard - handles auth, permissions, and redirects only
// Data loading is handled by AuthenticatedLayout
router.beforeEach(async (to, from, next) => {
    // SignOut.vue tears the session down itself; skip the user fetch below
    if (to.name === 'signout') {
        return next();
    }

    const authStore = useAuthenticationStore();
    const isAuthenticated = authStore.isAuthenticated();
    const requiresAuth = to.matched.some((r) => r.meta.authenticated);
    const isCommunityRoute = to.matched.some((r) => r.meta.communityLevel);
    const onCommunityDomain = EnvironmentService.isCommunityDomain();

    // Redirect unauthenticated users
    if (requiresAuth && !isAuthenticated) {
        // Expired session with data still in memory: clear it before the next sign-in
        return next({ name: authStore.user ? 'signout' : 'signin' });
    }

    if (!isAuthenticated) {
        return next();
    }

    // Fetch user data if needed
    if (!authStore.user || !authStore.hasFetchedCurrentUser) {
        try {
            await authStore.fetchCurrentUser();
        } catch (error) {
            console.error('[Router] Failed to fetch user:', error);
            return next({ name: 'signout' });
        }
    }

    const user = authStore.user;
    if (!user) {
        return next({ name: 'signout' });
    }

    // On a community subdomain, non-members of that community get no further
    const membership = UserPermissionsService.getCurrentCommunityMembership(user);
    if (requiresAuth && onCommunityDomain && !membership) {
        return next({ name: 'no-access', replace: true });
    }

    // Users without an Operator/Admin/Owner role only get the welcome page
    const allowsAllRoles = to.matched.some((r) => r.meta.allowAllRoles);
    if (requiresAuth && !allowsAllRoles && !UserPermissionsService.hasOperatorOrAdminRole(user)) {
        return next({ name: 'welcome', replace: true });
    }

    // Redirect from signin/signup if already authenticated
    if (to.name === 'signin' || to.name === 'signup') {
        return next({ name: onCommunityDomain ? 'operator-home-redirect' : 'app-dashboard' });
    }

    const isAppDashboardRedirect = onCommunityDomain && to.name === 'app-dashboard';

    // Community-level permission checks
    if (isCommunityRoute || isAppDashboardRedirect) {
        if (!membership) {
            return next({ name: 'no-access' });
        }

        // Members without an operator role in this community
        if (!UserPermissionsService.canAccessCommunity(user, membership.community.id)) {
            return next({ name: 'welcome', replace: true });
        }

        // Check installer access if required
        const requiresAdmin = to.matched.some((r) => r.meta.requiresAdmin);
        if (requiresAdmin && !UserPermissionsService.canAccessInstallerForCommunity(user, membership.community.id)) {
            const venueId = to.params.venueId || UserPermissionsService.getDefaultVenueId(user);
            return next({ name: 'operator-home', params: { venueId }, replace: true });
        }

        // Handle redirect routes (legacy URLs without venue ID, and root domain redirects)
        const redirectToMode = isAppDashboardRedirect
            ? 'operator'
            : to.matched.find(r => r.meta.redirectToMode)?.meta.redirectToMode;

        if (redirectToMode) {
            const defaultVenueId = UserPermissionsService.getDefaultVenueId(user);
            if (!defaultVenueId) {
                // No venue data in /me response — let the user through.
                // AuthenticatedLayout will fetch venues and handle navigation.
                return next();
            }

            const canAccessInstaller = UserPermissionsService.canAccessInstallerForCommunity(user, membership.community.id);

            // Fall back to operator if no installer access
            const targetMode = (redirectToMode === 'installer' && !canAccessInstaller) ? 'operator' : redirectToMode;
            const routeName = targetMode === 'installer' ? 'installer-home' : 'operator-home';

            return next({ name: routeName, params: { venueId: defaultVenueId }, replace: true });
        }
    }

    next();
});

export default router;
